import { createSelector } from 'reselect';
import {
  RootState, LocationState, ListView, ListViewRow, LocationFacetsState, LocationFacetsData
} from '../model/state';
import { User, getRoleFromPermissions, Location, Country, PermissionsEnum, isLocationFullAccess, getRoleRelevanceForSorting, LocationRole, LocationType, typeToString } from 'model';
import { selectLoggedUser, selectLoggedUserLocationRolesMap } from './Auth';
import { selectCountryList } from './Common';
import { selectCurrentUser } from './User';


/**
 * Direct selector to the state
 */
const selectLocation = () => (state: RootState) => state.location;

/**
 * Other specific selectors
 */
const selectLocationList = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.list
);

const selectSingleSelectLocation = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.singleSelectLocation
);

const selectIsSelectedAll = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.isAllSelected
);

const selectIsSelectedAny = () => createSelector(
  selectLocationList(),
  (locations: ListView<Location>) => locations.rows?.find(loc => loc.isSelected) !== undefined
);

const selectSingleSelectOrSelectedItems = () => createSelector(
  selectSingleSelectLocation(),
  selectLocationList(),
  (singleSelectLocation: Location | null, locations:ListView<Location>) => {
    return singleSelectLocation
      ? [singleSelectLocation]
      : locations.rows?.filter(loc => loc.isSelected) || [];
  }
);

const selectSelectedItems = () => createSelector(
  selectLocationList(),
  (locations: ListView<Location>) => locations.rows?.filter(loc => loc.isSelected) || []
);

const selectSelectedNumber = () => createSelector(
  selectLocationList(),
  (locations: ListView<Location>) => (locations.rows?.filter(loc => loc.isSelected) || []).length
);

const selectUserLocations = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.userLocations
);

const selectParent = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.parent
);

const selectLocationSearch = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.search
);

const selectIsGrantFullAccessEnabled = () => createSelector(
  selectSelectedItems(),
  selectCurrentUser(),
  selectLoggedUserLocationRolesMap(),
  selectLoggedUser(),
  (selected: Array<ListViewRow<Location>>, currentUser: User, roleMap: {[key: string]: LocationRole}, authUser?: User) => {
    const invalidAuthorizer = selected.find(loc => roleMap[loc.id || ''] ? getRoleFromPermissions(roleMap[loc.id || ''].roles) !== 'fullaccess' : true);
    return  (invalidAuthorizer === undefined) &&
            (currentUser.id !== authUser?.id) &&
            !(currentUser.accountRole?.roles || []).includes(PermissionsEnum.OWNER);
  }
);

const selectIsGrantLimitedAccessEnabled = () => createSelector(
  selectCurrentUser(),
  selectLoggedUser(),
  (currentUser: User, authUser?: User) => {
     return (currentUser.id !== authUser?.id) &&
            !(currentUser.accountRole?.roles || []).includes(PermissionsEnum.OWNER);
  }
);

const selectIsRevokeAccessEnabled = () => createSelector(
  selectCurrentUser(),
  selectLoggedUser(),
  (currentUser: User, authUser?: User) => {
     return (currentUser.id !== authUser?.id) &&
            !(currentUser.accountRole?.roles || []).includes(PermissionsEnum.OWNER);
  }
);

const selectAreAllFullAccessLocations = () => createSelector(
  selectSelectedItems(),
  (selected: Array<ListViewRow<Location>>) => {
    const noFullAccessLocation = selected.find(loc => !isLocationFullAccess(loc));
    return noFullAccessLocation === undefined;
  }
);

const selectSelectedLocations = () => createSelector(
  selectSelectedItems(),
  (locations: Array<ListViewRow<Location>>) => locations.map(l => l as Location)
);

const selectLocationNotFiltered = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.list
);

const selectLocationFacets = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.facets
);

const selectLocationFilterData = () => createSelector(
  selectLocationNotFiltered(),
  selectParent(),
  selectLocationFacets(),
  selectCountryList(),
  (locations: ListView<Location>, parent: Location | null, facets: LocationFacetsState ,countries?: Country[]): LocationFacetsData => {
    return {
      cities: {
        ...facets.city,
        rows: facets.city?.rows?.map(l => ({value: l, label: l})) || []
      },
      states: {
        ...facets.state,
        rows: facets.state?.rows?.map(l => ({value: l, label: l.toUpperCase()})) || []
      },
      countries: {
        ...facets.country,
        rows: facets.country?.rows?.map(l => ({value: l, label: countries?.find(c => c.id === l)?.name || l})) || []
      },
      postalCodes: {
        ...facets.postalCode,
        rows: facets.postalCode?.rows?.map(l => ({value: l, label: l})) || []
      },
      types: {
        ...facets.class,
        rows: facets.class?.rows?.map(l => ({value: l.toUpperCase(), label: typeToString(LocationType[l.toUpperCase() as keyof typeof LocationType])})) || []
      },
    }
  }
);

const selectLocationFilters = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.filters
);

const selectCurrentLocation = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.currentLocation
);

const selectLocationRolesOrderedList = () => createSelector(
  selectLocationList(),
  (locations: ListView<Location>) => ({
    ...locations,
    rows: locations.rows?.sort((a, b) => getRoleRelevanceForSorting(b.roles || []) - getRoleRelevanceForSorting(a.roles || []))
  }));

const selectShowLocationFilters = () => createSelector(
  selectLocation(),
  (location: LocationState) => location.showFilters
);

export default selectLocation;
export {
  selectLocationList,
  selectSingleSelectLocation,
  selectIsSelectedAll,
  selectIsSelectedAny,
  selectSelectedNumber,
  selectUserLocations,
  selectSingleSelectOrSelectedItems,
  selectSelectedItems,
  selectParent,
  selectLocationSearch,
  selectIsGrantFullAccessEnabled,
  selectIsGrantLimitedAccessEnabled,
  selectIsRevokeAccessEnabled,
  selectSelectedLocations,
  selectLocationFilterData,
  selectLocationFilters,
  selectCurrentLocation,
  selectAreAllFullAccessLocations,
  selectLocationRolesOrderedList,
  selectShowLocationFilters,
}
